import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PageHeader } from "@features/shared";
import { Container } from "react-bootstrap";
import { Tabs } from "@features/shared/components";
import { CustomButton, CustomFileUpload } from "@shared/components";
import {
  Dialog, DialogTitle, DialogContent,
  IconButton, Fab, Typography, Alert, Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  fetchComplianceStart, fetchComplianceSuccess, fetchComplianceFailure,
  uploadStart, uploadSuccess, uploadFailure,
} from "../../Redux/slices/oaapSlice";
import { getComplianceStatusApi } from "../../services/oaap/complianceApi";
import { uploadOaapApi, replaceOaapApi } from "../../services/oaap/uploadOaapApi";
import useNotification from "@shared/hooks/useNotification";
import MonthYearSelector from "./components/shared/MonthYearSelector";
import ReplaceConfirmModal from "./components/shared/ReplaceConfirmModal";
import BMOAAPList from "./components/bm/BMOAAPList";
import BMOAAPUpload from "./components/bm/BMOAAPUpload";
import BMFeedbackView from "./components/bm/BMFeedbackView";
import ZMReviewList from "./components/zm/ZMReviewList";
import ZMOAAPList from "./components/zm/ZMOAAPList";
import NSMReviewList from "./components/nsm/NSMReviewList";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// ─────────────────────────────────────────────────────────────────────────────
// ZM Upload component (same pattern as BM, role = SH5 or SH6)
// ─────────────────────────────────────────────────────────────────────────────
const ZMOAAPUpload = ({ userCode, userEmail, userName, salesGroupCode, division, clusterCode, userType, onSuccess }) => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();
  const { submissions, uploadLoading } = useSelector((s) => s.oaap);
  const sfToken = useSelector((s) => s?.tokens?.accessToken || "");

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [file, setFile] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  const existingSubmission = submissions.find(
    (s) => s.user_code === userCode && s.month === month && s.year === year && s.is_active
  );

  const handleFileChange = (files) => {
    if (files && files[0]) setFile(files[0]);
  };

  const handleSubmit = () => {
    if (!file) { showError("Please select a file"); return; }
    if (!month || !year) { showError("Please select month and year"); return; }

    // Guard: block past-month submissions
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      showError("Submissions are only allowed for the current month and future months.");
      return;
    }

    if (existingSubmission) { setShowReplaceModal(true); return; }
    doUpload(false);
  };

  const doUpload = async (replacing) => {
    setShowReplaceModal(false);
    dispatch(uploadStart());
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userCode", userCode);
    formData.append("userEmail", userEmail || "");
    formData.append("userName", userName);
    formData.append("role", userType || "SH5");
    formData.append("salesGroupCode", salesGroupCode || "");
    if (division) formData.append("division", String(division));
    formData.append("clusterCode", clusterCode || "");
    formData.append("month", String(month));
    formData.append("year", String(year));
    if (sfToken) formData.append("sfToken", sfToken);
    try {
      const res = replacing && existingSubmission
        ? await replaceOaapApi(existingSubmission.id, formData)
        : await uploadOaapApi(formData);
      dispatch(uploadSuccess(res.data));
      showSuccess(`OAAP for ${MONTH_NAMES[month - 1]} ${year} ${replacing ? "replaced" : "uploaded"} successfully`);
      setFile(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.msg || "Upload failed";
      dispatch(uploadFailure(msg));
      showError(msg);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MonthYearSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} disabled={uploadLoading} disablePast />
      {existingSubmission && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          An OAAP is already submitted for {MONTH_NAMES[month - 1]} {year}. Uploading will replace it.
        </Alert>
      )}
      <Box>
        <Typography variant="body2" fontWeight={500} gutterBottom>
          Select File <span style={{ color: "#d32f2f" }}>*</span>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            (PDF, Excel or Word, max 20 MB)
          </Typography>
        </Typography>
        <CustomFileUpload
          onChange={handleFileChange}
          accept=".pdf,.xlsx,.xls,.doc,.docx"
          maxSize={20}
          disabled={uploadLoading}
        />
      </Box>
      <CustomButton
        onClick={handleSubmit}
        disabled={uploadLoading || !file}
        fullWidth
        loading={uploadLoading}
      >
        {uploadLoading ? "Submitting\u2026" : existingSubmission ? "Replace" : "Submit"}
      </CustomButton>
      <ReplaceConfirmModal
        isOpen={showReplaceModal}
        onClose={() => setShowReplaceModal(false)}
        onConfirm={() => doUpload(true)}
        loading={uploadLoading}
        month={MONTH_NAMES[month - 1]}
        year={year}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BM View (SH3 / SH4)
// ─────────────────────────────────────────────────────────────────────────────
const BMView = ({ userDetails, userCode, userType, complianceStatus, complianceLoading }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [feedbackSubmission, setFeedbackSubmission] = useState(null);
  const { showError } = useNotification();

  const today = new Date();
  const filterMonth = today.getMonth() + 1;
  const filterYear = today.getFullYear();
  const bmClusterCode = userDetails?.SH_Code__c || "";

  const handleOpenUpload = () => {
    if (!bmClusterCode) {
      showError("Your hierarchy (SH Code) is not set up. Please contact support.");
      return;
    }
    setUploadOpen(true);
  };

  return (
    <div className="space-y-4 pb-24">
      <BMOAAPList
        userCode={userCode}
        selectedMonth={filterMonth}
        selectedYear={filterYear}
        onViewFeedback={(sub) => setFeedbackSubmission(sub)}
      />

      {/* Floating + FAB */}
      <Fab
        onClick={handleOpenUpload}
        aria-label="Upload OAAP"
        sx={{
          position: "fixed",
          bottom: 80,
          right: 20,
          backgroundColor: "#3901ff",
          color: "#fff",
          boxShadow: "0 4px 14px rgba(57,1,255,0.35)",
          zIndex: 55,
          "&:hover": { backgroundColor: "#3001dd" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Upload modal */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 2.5, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <Typography variant="subtitle2" fontWeight={600}>Submit My OAAP</Typography>
          <IconButton size="small" onClick={() => setUploadOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2.5, paddingTop: "8px !important" }}>
          <BMOAAPUpload
            userCode={userCode}
            userEmail={userDetails?.Email || ""}
            userName={userDetails?.Name || ""}
            salesGroupCode={userDetails?.Sales_Group_Code__c || ""}
            division={userDetails?.Division || null}
            clusterCode={bmClusterCode}
            userType={userType}
            onSuccess={() => setUploadOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <BMFeedbackView
        submission={feedbackSubmission}
        isOpen={!!feedbackSubmission}
        onClose={() => setFeedbackSubmission(null)}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ZM View (SH5)
// ─────────────────────────────────────────────────────────────────────────────
const ZMView = ({ userDetails, userCode, userType, complianceStatus, complianceLoading }) => {
  const [activeTab, setActiveTab] = useState("review");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [feedbackSubmission, setFeedbackSubmission] = useState(null);

  const shCode = userDetails?.SH_Code__c || "";
  const clusterCode = shCode;

  const today = new Date();
  const filterMonth = today.getMonth() + 1;
  const filterYear = today.getFullYear();

  const tabs = ["Review BMs", "My OAAPs"];
  const tabIdMap = { "Review BMs": "review", "My OAAPs": "list" };

  return (
    <div className="space-y-4 pb-24">
      <Tabs
        tabs={tabs}
        activeTab={tabs.find((t) => tabIdMap[t] === activeTab)}
        onChange={(label) => setActiveTab(tabIdMap[label])}
      />

      {activeTab === "review" && (
        <ZMReviewList
          reviewerCode={userCode}
          reviewerName={userDetails?.Name || ""}
          reviewerRole={userType}
          userEmail={userDetails?.Email || ""}
        />
      )}
      {activeTab === "list" && (
        <ZMOAAPList
          userCode={userCode}
          selectedMonth={filterMonth}
          selectedYear={filterYear}
          onViewFeedback={(sub) => setFeedbackSubmission(sub)}
        />
      )}

      <BMFeedbackView
        submission={feedbackSubmission}
        isOpen={!!feedbackSubmission}
        onClose={() => setFeedbackSubmission(null)}
        title="NSM Feedback"
      />
      {activeTab === "list" && (
        <Fab
          onClick={() => setUploadOpen(true)}
          aria-label="Upload OAAP"
          sx={{
            position: "fixed",
            bottom: 80,
            right: 20,
            backgroundColor: "#3901ff",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(57,1,255,0.35)",
            zIndex: 55,
            "&:hover": { backgroundColor: "#3001dd" },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Upload modal */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 2.5, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <Typography variant="subtitle2" fontWeight={600}>Submit My OAAP</Typography>
          <IconButton size="small" onClick={() => setUploadOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2.5, paddingTop: "12px !important" }}>
          <ZMOAAPUpload
            userCode={userCode}
            userEmail={userDetails?.Email || ""}
            userName={userDetails?.Name || ""}
            salesGroupCode={userDetails?.Sales_Group_Code__c || ""}
            division={userDetails?.Division || null}
            clusterCode={clusterCode}
            userType={userType}
            onSuccess={() => setUploadOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NSM View (SH7)
// ─────────────────────────────────────────────────────────────────────────────
const NSMView = ({ userDetails, userCode }) => {
  return (
    <NSMReviewList
      reviewerCode={userCode}
      reviewerName={userDetails?.Name || ""}
      userEmail={userDetails?.Email || ""}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main OAAP Page
// ─────────────────────────────────────────────────────────────────────────────
const OAAPPage = () => {
  const dispatch = useDispatch();
  const userType = useSelector((state) =>
    state?.userType?.type !== "Other" ? state?.userType?.type : state?.userDetails?.details?.Title
  );
  const userDetails = useSelector((state) => state?.userDetails?.details);
  const { complianceStatus, complianceLoading } = useSelector((s) => s.oaap);

  const userCode = userDetails?.Id || "";

  // Poll compliance status on page load
  useEffect(() => {
    if (!userCode || !["SH3", "SH4", "SH5", "SH6"].includes(userType)) return;
    const today = new Date();
    dispatch(fetchComplianceStart());
    getComplianceStatusApi({
      userCode,
      role: userType,
      division: userDetails?.Division || undefined,
      month: today.getMonth() + 1,
      year: today.getFullYear(),
    })
      .then((res) => dispatch(fetchComplianceSuccess(res.data)))
      .catch(() => dispatch(fetchComplianceFailure(null)));
  }, [dispatch, userCode, userType, userDetails]);

  const renderContent = () => {
    if (userType === "SH3" || userType === "SH4") {
      return <BMView userDetails={userDetails} userCode={userCode} userType={userType} complianceStatus={complianceStatus} complianceLoading={complianceLoading} />;
    }
    if (userType === "SH5" || userType === "SH6") {
      return <ZMView userDetails={userDetails} userCode={userCode} userType={userType} complianceStatus={complianceStatus} complianceLoading={complianceLoading} />;
    }
    if (userType === "SH7") {
      return <NSMView userDetails={userDetails} userCode={userCode} />;
    }
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
        OAAP is not available for your role.
      </Typography>
    );
  };

  return (
    <div>
      <PageHeader title="OAAP" path="/" />
      <Container style={{ backgroundColor: "#F5F5F5", minHeight: "80vh" }} className="py-4">
        {renderContent()}
      </Container>
    </div>
  );
};

export default OAAPPage;
